// Pure rules of the Components item record (H100) — kept out of the JSX so the
// legacy gates are testable one by one.
import type { ItemList } from "../../inventory/types";
import { AIRCRAFT_RETURN_TABS, type AircraftReturnTab } from "../../utils/recordReturn";

export type ComponentTabKey = "general" | "sub" | "aircraft" | "history" | "file";

export interface ComponentTabDef {
  key: ComponentTabKey;
  label: string;
}

/** Legacy tab order on the record Aaron screenshotted (demo.aeronet.nz, 2026-09-28):
 * General · Sub Components · Aircraft Item Details · Component History · File.
 * Sources: mod_parts/parts_config.inc:76-110 (whcfgtab, mode=edit_item). */
export const COMPONENT_TABS: ComponentTabDef[] = [
  { key: "general", label: "General" },
  { key: "sub", label: "Sub Components" },
  { key: "aircraft", label: "Aircraft Item Details" },
  { key: "history", label: "Component History" },
  { key: "file", label: "File" },
];

/** Legacy `modcfg.parts.status_for_maintenance_tabs`, default 7,16,17,18
 * (Flying, Removed, Rotable, Retired). */
export function maintenanceStatuses(config?: { [key: string]: any } | null): number[] {
  const raw = config?.status_for_maintenance_tabs;
  const list = Array.isArray(raw)
    ? raw
    : typeof raw === "string" && raw.trim()
    ? raw.split(",")
    : typeof raw === "number"
    ? [raw]
    : ["7", "16", "17", "18"];
  return list.map((v) => Number(String(v).trim())).filter((n) => !Number.isNaN(n));
}

/** Which tabs legacy shows for this item:
 * - Sub Components / Aircraft Item Details / Component History only at a
 *   maintenance status (parts_tab_item_sub_component.inc:1-5,
 *   parts_tab_item_aircraft.inc:23-27, parts_tab_component_history.inc:1-5);
 * - Aircraft Item Details only when the item is on an aircraft — legacy drops
 *   every tab whose heading contains "aircraft" when items.aircraft_id is empty
 *   (parts_edit.inc:208-226). v6's item read carries the aircraft as
 *   `aircraft_obj`, present only when the item has one.
 * NOT honoured: the tenant's legacy `tabs` rows (active/priority per install) —
 * v6 serves no read of `parts`/`edit_item` rows. */
export function visibleComponentTabs(
  item: Pick<ItemList, "status"> & { aircraft_obj?: { registration?: string | null } | null },
  config?: { [key: string]: any } | null
): ComponentTabDef[] {
  const maintenance = maintenanceStatuses(config).includes(Number(item.status));
  const onAircraft = Boolean(item.aircraft_obj);
  return COMPONENT_TABS.filter((t) => {
    if (t.key === "sub" || t.key === "history") return maintenance;
    if (t.key === "aircraft") return maintenance && onAircraft;
    return true;
  });
}

const isYes = (v: unknown) => /^(yes|1|true)$/i.test(String(v ?? "").trim());

/** Legacy header: "Item #: <id>" + "<part #> / <serial> / <batch>"
 * (parts_tab_general_item.inc:198-202 into parts_edit.inc:124-125). A blank
 * serial prints N/A unless parts.display_blank_serial_nos is Yes (:6, :59). */
export function componentRecordTitle(
  item: Pick<ItemList, "id" | "serial_number" | "release_number" | "batch"> & {
    part?: { number?: string | null } | null;
  },
  config?: { [key: string]: any } | null
): string {
  const serial =
    item.serial_number && String(item.serial_number).trim()
      ? item.serial_number
      : isYes(config?.display_blank_serial_nos)
      ? ""
      : "N/A";
  const batch = item.batch?.number ?? item.release_number ?? "";
  return `Item #: ${item.id} ${item.part?.number ?? ""} / ${serial} / ${batch}`;
}

/** Legacy validate_sn() (mod_parts/js/parts_edit.js:127-142): a Serial # change needs a
 * reason unless the old AND the new value are both blank or in parts.na_sn_list (exact
 * match, default "NA,NSN,UNK,N/A"). The backend applies the same rule
 * (items/api.py serial_change_needs_reason). */
export function serialChangeNeedsReason(
  oldSn: string | null | undefined,
  newSn: string | null | undefined,
  naSnList?: string | null
): boolean {
  const before = oldSn ?? "";
  const after = newSn ?? "";
  if (before === after) return false;
  const na = (naSnList ?? "NA,NSN,UNK,N/A,").split(",");
  const real = (sn: string) => !na.includes(sn) && sn.trim() !== "";
  return real(after) || real(before);
}

// ── Where the record came from, and where Close goes (BP-97) ─────────────────
// Aaron 2026-10-07: "edit the component in components should return to the components
// list", and the aircraft's Components tab opens this same record ("editing a component
// from the aircraft should be the same") — Aircraft and Components are dependent modules,
// so the aircraft links straight here and Close goes back to its Components tab.

/** The Components lists that open the record, each its own mode route. */
export const COMPONENT_LIST_MODES = ["parts", "flying", "removed", "rotable", "retired"] as const;
export type ComponentListMode = (typeof COMPONENT_LIST_MODES)[number];

export type ComponentRecordOrigin =
  | { kind: "list"; mode: ComponentListMode }
  /** `tab`: the aircraft tab the record was opened from, when not Components (the
   *  Maintenance Log's "Open", 2026-10-12); Close returns there. */
  | { kind: "aircraft"; aircraftId: number; tab?: AircraftReturnTab };

/** The record's own URL (absolute), carrying its origin. */
export function componentRecordUrl(id: number | string, origin: ComponentRecordOrigin): string {
  if (origin.kind === "list") return `/components/items/${id}?from=${origin.mode}`;
  const base = `/components/items/${id}?from=aircraft&aircraft=${origin.aircraftId}`;
  return origin.tab && origin.tab !== "Components" ? `${base}&from_tab=${encodeURIComponent(origin.tab)}` : base;
}

/** Read the origin back off the record's query. Anything unrecognised is the
 * Components / Parts list (the module's index). */
export function componentRecordOrigin(params: URLSearchParams): ComponentRecordOrigin {
  const from = params.get("from") || "";
  const aircraftId = Number(params.get("aircraft"));
  if (from === "aircraft" && Number.isInteger(aircraftId) && aircraftId > 0) {
    const tab = params.get("from_tab");
    return tab && AIRCRAFT_RETURN_TABS.includes(tab as AircraftReturnTab)
      ? { kind: "aircraft", aircraftId, tab: tab as AircraftReturnTab }
      : { kind: "aircraft", aircraftId };
  }
  const mode = (COMPONENT_LIST_MODES as readonly string[]).includes(from) ? (from as ComponentListMode) : "parts";
  return { kind: "list", mode };
}

/** Close / Save & Close: back to the list it came from, or the aircraft's Components tab. */
export function componentRecordCloseUrl(origin: ComponentRecordOrigin): string {
  if (origin.kind === "list") return `/components/${origin.mode}`;
  return `/aircraft/${origin.aircraftId}?tab=${origin.tab ? encodeURIComponent(origin.tab) : "components"}`;
}
